/**
 * Formats a date string into a relative time format like "1d", "2mo", "3y"
 * @param dateString - The date string to format
 * @returns A human-readable relative time string
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();

  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30.44); // Average days per month
  const diffInYears = Math.floor(diffInDays / 365.25); // Account for leap years

  if (diffInYears > 0) {
    return `${diffInYears}y`;
  } else if (diffInMonths > 0) {
    return `${diffInMonths}mo`; // Use "mo" instead of "m" to distinguish from minutes
  } else if (diffInDays > 0) {
    return `${diffInDays}d`;
  } else {
    return 'Today';
  }
}

/**
 * Formats a duration in seconds into a concise, readable format
 * If duration is >= 1 hour, returns format like "1h 23m" or "2h 5m"
 * If duration is < 1 hour, returns format like "45m" or "5m"
 * @param seconds - The duration in seconds
 * @returns A formatted duration string
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
}

/**
 * Formats a date string into a short relative time format like "6h", "3d", "2w", "1mo"
 * Similar to the 'mini' format in javascript-time-ago library
 * @param dateString - The date string to format (should be UTC/ISO format)
 * @returns A short human-readable relative time string
 */
export function formatShortTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  
  // Handle future dates by treating them as "now"
  if (date.getTime() > now.getTime()) {
    return 'now';
  }
  
  const diffInMs = now.getTime() - date.getTime();
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30.44);
  const diffInYears = Math.floor(diffInDays / 365.25);
  
  if (diffInSeconds < 60) {
    return 'now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d`;
  } else if (diffInMonths < 1) {
    // Less than a month: show weeks (avoids "0mo" edge case around 28-30 days)
    return `${diffInWeeks}w`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths}mo`;
  } else {
    return `${diffInYears}y`;
  }
}

/**
 * Timezone-aware date formatting using Intl.DateTimeFormat.
 * All functions accept an optional IANA timezone string (e.g. 'America/New_York', 'Asia/Shanghai').
 * If no timezone is provided, falls back to the browser's local timezone.
 *
 * A value that is not a date — undefined, '', a malformed string — renders as
 * `NO_DATE` rather than throwing. `Intl.DateTimeFormat.format` raises a
 * RangeError on an invalid time value, so an absent nullable timestamp
 * (`enabled_at`, `trusted_until`, an unsent invitation's `expires_at`) took
 * down the whole page it appeared on. These are display helpers: the honest
 * answer for "no date" is a dash in that cell.
 */

/** What a date cell shows when there is no date to show. */
export const NO_DATE = '—';

function formatInTz(
  dateString: string,
  timezone: string | null | undefined,
  opts: Intl.DateTimeFormatOptions,
): string {
  // `new Date(null)` is the epoch, not an invalid date, so a nullable column
  // that arrived as null would have rendered "Jan 1, 1970" — checked before
  // parsing rather than after.
  if (!dateString) return NO_DATE;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return NO_DATE;
  return new Intl.DateTimeFormat('en-US', {
    ...opts,
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(date);
}

/**
 * Format: "MMM D, YYYY h:mm:ss A ZZZ" in the given timezone, including the short
 * timezone name so the full date row is unambiguous about which zone it's in.
 * Example: "Feb 21, 2026 3:59:15 PM EST"
 */
export function formatDateTimeWithSeconds(dateString: string, timezone?: string | null): string {
  return formatInTz(dateString, timezone, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true, timeZoneName: 'short' });
}

/**
 * Format: "MMM D, YYYY h:mm A" in the given timezone.
 * Example: "Feb 21, 2026 3:59 PM"
 */
export function formatDateTimeInTz(dateString: string, timezone?: string | null): string {
  return formatInTz(dateString, timezone, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

/**
 * Format: "MMM D, YYYY h:mm A ZZZ" in the given timezone, including the short
 * timezone name so a reader knows which zone the time is expressed in.
 * Example: "Feb 21, 2026 3:59 PM EST"
 */
export function formatDateTimeWithZoneInTz(dateString: string, timezone?: string | null): string {
  return formatInTz(dateString, timezone, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' });
}

/**
 * Format: "MMM D, YYYY" in the given timezone.
 * Example: "Feb 21, 2026"
 */
export function formatDateInTz(dateString: string, timezone?: string | null): string {
  return formatInTz(dateString, timezone, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Format: "ddd, MMM D, YYYY at h:mm A" in the given timezone.
 * Example: "Sat, Feb 21, 2026 at 3:59 PM"
 */
export function formatDateTimeFullInTz(dateString: string, timezone?: string | null): string {
  const formatted = formatInTz(dateString, timezone, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  // Intl produces "Sat, Feb 21, 2026, 3:59 PM"; this format wants "… at 3:59 PM".
  // Replace the comma before the time portion.
  return formatted.replace(/,\s*(\d{1,2}:\d{2}\s*[AP]M)/, ' at $1');
}

/**
 * Format: "MMMM D, YYYY" in the given timezone (long month name).
 * Example: "February 21, 2026"
 */
export function formatDateLongInTz(dateString: string, timezone?: string | null): string {
  return formatInTz(dateString, timezone, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * Format a future time difference as a human-readable relative string.
 */
function formatFutureRelative(diffInMs: number): string {
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 60) {
    return diffInMinutes <= 1 ? 'in 1 minute' : `in ${diffInMinutes} minutes`;
  } else if (diffInHours < 24) {
    return diffInHours === 1 ? 'in 1 hour' : `in ${diffInHours} hours`;
  } else if (diffInDays === 1) {
    return 'tomorrow';
  } else if (diffInDays < 7) {
    return `in ${diffInDays} days`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return weeks === 1 ? 'in 1 week' : `in ${weeks} weeks`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return months === 1 ? 'in 1 month' : `in ${months} months`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return years === 1 ? 'in 1 year' : `in ${years} years`;
  }
}

/**
 * Timezone-aware scheduled date formatting.
 * Shows both the absolute date/time and relative time.
 */
export function formatScheduledDateInTz(dateString: string, timezone?: string | null): string {
  if (!dateString) return NO_DATE;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return NO_DATE;
  const now = new Date();
  const absoluteFormat = formatDateTimeInTz(dateString, timezone);
  const diffInMs = date.getTime() - now.getTime();

  if (diffInMs <= 0) {
    return `${absoluteFormat} (past due)`;
  }

  return `${absoluteFormat} (${formatFutureRelative(diffInMs)})`;
}